// data/carecopilotSources.js
//
// Curated, verified medical sources shown alongside Care Copilot responses
// and lab AI analysis, addressing App Store Guideline 1.4.1 (medical
// information must include citations to sources).
//
// IMPORTANT: every URL here was verified via live web search before being
// added — do not add a new source without confirming the exact URL works,
// since a broken/wrong link in a healthcare app is worse than no citation.
//
// This is intentionally a static, hand-curated list — NOT generated by
// Claude per-response. Care Copilot never invents a citation; it only ever
// references what's in this pre-vetted list. This is the safe, fast path
// to Apple compliance discussed in-session — the long-term upgrade (a
// backend-stored library reviewed via the Content Review workflow, with
// Dr. Sarswat's clinical sign-off) can replace this later without changing
// how the UI consumes it.

export const SOURCES = {
  clinicalTrialsGov: {
    name: 'ClinicalTrials.gov (U.S. National Library of Medicine)',
    url: 'https://clinicaltrials.gov/',
  },
  labTestsOnline: {
    name: 'Lab Tests Online (American Association for Clinical Chemistry)',
    url: 'https://labtestsonline.org/',
  },
  fda: {
    name: 'U.S. Food & Drug Administration',
    url: 'https://www.fda.gov/',
  },
  medlinePlusCKD: {
    name: 'MedlinePlus — Chronic Kidney Disease',
    url: 'https://medlineplus.gov/chronickidneydisease.html',
  },
  niddkCKD: {
    name: 'NIDDK — Chronic Kidney Disease (CKD)',
    url: 'https://www.niddk.nih.gov/health-information/kidney-disease/chronic-kidney-disease-ckd',
  },
  niddkManaging: {
    name: 'NIDDK — Managing Chronic Kidney Disease',
    url: 'https://www.niddk.nih.gov/health-information/kidney-disease/chronic-kidney-disease-ckd/managing',
  },
  kidneyOrgCKD: {
    name: 'National Kidney Foundation — Chronic Kidney Disease',
    url: 'https://www.kidney.org/kidney-topics/chronic-kidney-disease-ckd',
  },
};

// Maps each Care Copilot context id (matches CONTEXTS in CareCopilot.js /
// CareCopilotWidget.js) to the sources relevant to that topic.
export const SOURCES_BY_CONTEXT = {
  general: [SOURCES.niddkCKD, SOURCES.medlinePlusCKD, SOURCES.kidneyOrgCKD],
  labs: [SOURCES.medlinePlusCKD, SOURCES.labTestsOnline, SOURCES.niddkCKD],
  diagnosis: [SOURCES.niddkCKD, SOURCES.medlinePlusCKD, SOURCES.kidneyOrgCKD],
  trials: [SOURCES.clinicalTrialsGov, SOURCES.niddkCKD],
  records: [SOURCES.medlinePlusCKD, SOURCES.niddkCKD],
  medications: [SOURCES.niddkManaging, SOURCES.fda],
  doctorvisit: [SOURCES.niddkManaging, SOURCES.kidneyOrgCKD],
  type: [SOURCES.niddkCKD, SOURCES.kidneyOrgCKD],
  profile: [SOURCES.niddkCKD, SOURCES.medlinePlusCKD],
};

// Fallback used for lab AI analysis results (outside Care Copilot's own
// context system) and as a default if a context id isn't found above.
// NOTE: this is what EducationShell renders in its "Sources & References"
// footer on every education page, so it needs to be the general CKD set —
// it's not context-specific like SOURCES_BY_CONTEXT below.
export const DEFAULT_SOURCES = [
  SOURCES.medlinePlusCKD,
  SOURCES.niddkCKD,
  SOURCES.kidneyOrgCKD,
  SOURCES.labTestsOnline,
];

export function getSourcesForContext(contextId) {
  return SOURCES_BY_CONTEXT[contextId] || DEFAULT_SOURCES;
}